import { useRef, useState } from 'react'
import { motion } from 'framer-motion'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, RadialBarChart, RadialBar,
} from 'recharts'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

// ── Types ──────────────────────────────────────────────────────────────────────

interface ProfileInfo {
  name: string
  username?: string
  bio?: string
  avatar?: string
  followers: number
  following?: number
  post_count?: number
  verified?: boolean
  category?: string
  total_views?: number
}

interface PerPost {
  index: number
  date: string
  likes: number
  comments: number
  views: number
  shares: number
}

interface EngagementStats {
  total_posts_analyzed: number
  avg_likes: number
  avg_comments: number
  avg_views: number
  avg_shares: number
  est_engagement_rate: number
  per_post: PerPost[]
}

interface ContentTheme {
  theme: string
  description: string
  frequency: 'high' | 'medium' | 'low'
}

interface RiskIndicator {
  indicator: string
  severity: 'high' | 'medium' | 'low'
  detail: string
}

interface IOC {
  type: string
  value: string
  context: string
}

interface InfluenceAssessment {
  reach: string
  engagement_quality: string
  authenticity_score: number
  notes: string
}

interface SubjectClassification {
  category: string
  confidence: number
  reasoning: string
}

export interface ReportData {
  platform: string
  identifier: string
  profile: ProfileInfo
  engagement_stats: EngagementStats
  posts_analyzed: number
  executive_summary: string
  behavioral_profile: string
  content_themes: ContentTheme[]
  top_hashtags: string[]
  risk_indicators: RiskIndicator[]
  key_findings: string[]
  iocs: IOC[]
  influence_assessment: InfluenceAssessment
  posting_pattern_analysis: string
  subject_classification: SubjectClassification
}

interface Props {
  reportData: ReportData
  rawData: any
  platform: string
  onClose: () => void
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const PLATFORM_COLOR: Record<string, string> = {
  facebook: '#3b82f6', instagram: '#ec4899', tiktok: '#14b8a6',
  youtube: '#ef4444', reddit: '#f97316',
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function reportId(): string {
  return Math.random().toString(16).slice(2, 10).toUpperCase()
}

function extractThumbnails(platform: string, raw: any): string[] {
  try {
    if (platform === 'tiktok') {
      const posts = raw?.posts?.itemList || raw?.posts?.data?.videos || (Array.isArray(raw?.posts) ? raw.posts : [])
      return (Array.isArray(posts) ? posts : []).slice(0, 8)
        .map((p: any) => p?.cover || p?.ai_dynamic_cover || p?.video?.cover || p?.video?.dynamicCover || '').filter(Boolean)
    }
    if (platform === 'instagram') {
      const posts = raw?.posts?.data || raw?.posts || []
      return (Array.isArray(posts) ? posts : []).slice(0, 8)
        .map((p: any) => p?.thumbnail_url || p?.display_url || p?.image_url || '').filter(Boolean)
    }
    if (platform === 'facebook') {
      const posts = raw?.posts?.data || raw?.posts || []
      return (Array.isArray(posts) ? posts : []).slice(0, 8)
        .map((p: any) => p?.full_picture || p?.picture || '').filter(Boolean)
    }
    if (platform === 'youtube') {
      const videos = raw?.videos?.items || []
      return (Array.isArray(videos) ? videos : []).slice(0, 8)
        .map((v: any) => v?.snippet?.thumbnails?.medium?.url || v?.snippet?.thumbnails?.default?.url || '').filter(Boolean)
    }
  } catch {}
  return []
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, string> = {
    high:   'bg-red-900/60 text-red-300 border-red-700/40',
    medium: 'bg-orange-900/60 text-orange-300 border-orange-700/40',
    low:    'bg-yellow-900/60 text-yellow-300 border-yellow-700/40',
  }
  return (
    <span className={`font-mono text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-widest ${map[severity] || 'bg-gray-900 text-gray-400 border-gray-700'}`}>
      {severity}
    </span>
  )
}

function ReachBadge({ reach }: { reach: string }) {
  const map: Record<string, string> = {
    micro: 'bg-blue-900/50 text-blue-300 border-blue-700/40',
    mid:   'bg-teal-900/50 text-teal-300 border-teal-700/40',
    macro: 'bg-purple-900/50 text-purple-300 border-purple-700/40',
    mega:  'bg-green-900/50 text-green-300 border-green-700/40',
  }
  return (
    <span className={`font-mono text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${map[reach] || 'bg-gray-800 text-gray-400 border-gray-700'}`}>
      {reach} reach
    </span>
  )
}

function FreqDot({ freq }: { freq: string }) {
  const map: Record<string, string> = { high: 'bg-green-400', medium: 'bg-yellow-400', low: 'bg-gray-500' }
  return <span className={`w-2 h-2 rounded-full shrink-0 ${map[freq] || 'bg-gray-600'}`} />
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-gray-950 border border-gray-700/60 rounded-lg px-3 py-2 text-xs font-mono space-y-1 shadow-xl">
      <div className="text-gray-400">Post #{label}</div>
      {payload.map((entry: any) => (
        <div key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {fmt(entry.value)}
        </div>
      ))}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ProfileIntelReport({ reportData, rawData, platform, onClose }: Props) {
  const reportRef = useRef<HTMLDivElement>(null)
  const [exporting, setExporting] = useState(false)
  const [reportIdStr] = useState(() => reportId())

  const { profile, engagement_stats, influence_assessment, subject_classification } = reportData
  const pColor = PLATFORM_COLOR[platform] || '#6b7280'
  const thumbnails = extractThumbnails(platform, rawData)

  const hasViews = engagement_stats.avg_views > 0
  const chartData = engagement_stats.per_post.slice(0, 15)

  const authScore = Math.min(100, Math.max(0, influence_assessment?.authenticity_score || 0))
  const gaugeData = [{ name: 'Auth', value: authScore, fill: authScore >= 70 ? '#22c55e' : authScore >= 40 ? '#f59e0b' : '#ef4444' }]

  const exportPDF = async () => {
    if (!reportRef.current) return
    setExporting(true)
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 1.8,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#050505',
        logging: false,
      })
      const imgData = canvas.toDataURL('image/jpeg', 0.88)
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' })
      const pW = pdf.internal.pageSize.getWidth()
      const pH = pdf.internal.pageSize.getHeight()
      const imgH = (canvas.height * pW) / canvas.width

      let heightLeft = imgH
      let pos = 0
      pdf.addImage(imgData, 'JPEG', 0, pos, pW, imgH)
      heightLeft -= pH
      while (heightLeft > 0) {
        pos -= pH
        pdf.addPage()
        pdf.addImage(imgData, 'JPEG', 0, pos, pW, imgH)
        heightLeft -= pH
      }
      pdf.save(`SYCEK-${platform.toUpperCase()}-${reportData.identifier}-${new Date().toISOString().slice(0, 10)}.pdf`)
    } finally {
      setExporting(false)
    }
  }

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `SYCEK-${platform}-${reportData.identifier}-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      {/* Action bar */}
      <div className="flex items-center gap-2 justify-between">
        <span className="font-mono text-xs text-gray-400 tracking-wider">PROFILE INTELLIGENCE REPORT</span>
        <div className="flex items-center gap-2">
          <button
            onClick={exportPDF}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-900/40 hover:bg-green-900/60 disabled:opacity-50 border border-green-700/40 text-green-300 font-mono text-[10px] font-bold rounded-lg transition-all"
          >
            {exporting ? (
              <><span className="w-3 h-3 border border-green-400 border-t-transparent rounded-full animate-spin" />EXPORTING...</>
            ) : (
              <>PDF EXPORT</>
            )}
          </button>
          <button
            onClick={exportJSON}
            className="px-3 py-1.5 bg-blue-900/30 hover:bg-blue-900/50 border border-blue-700/30 text-blue-400 font-mono text-[10px] font-bold rounded-lg transition-all"
          >
            JSON
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-gray-800/40 hover:bg-gray-800/60 border border-gray-700/30 text-gray-400 font-mono text-[10px] rounded-lg transition-all"
          >
            CLOSE
          </button>
        </div>
      </div>

      {/* Printable report */}
      <div ref={reportRef} className="bg-[#050505] border border-gray-800/50 rounded-2xl overflow-hidden">

        {/* ── Report header ── */}
        <div className="px-6 py-5 border-b border-gray-800/50 bg-black/60"
          style={{ background: `linear-gradient(135deg, #050505 0%, ${pColor}0a 100%)` }}>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-3">
                <span className="font-mono text-white font-black text-lg tracking-tight">SYCEK</span>
                <span className="font-mono text-[10px] text-gray-600 tracking-[0.2em]">OSINT PLATFORM</span>
                <span className="font-mono text-[9px] font-bold px-2 py-0.5 rounded border bg-amber-900/40 text-amber-300 border-amber-700/40 tracking-widest">TLP:AMBER</span>
              </div>
              <div className="font-mono text-[11px] text-gray-500 tracking-[0.15em]">SOCIAL TARGET INTELLIGENCE REPORT</div>
            </div>
            <div className="text-right space-y-1 shrink-0">
              <div className="font-mono text-[9px] text-gray-600">REPORT ID: <span className="text-gray-400">{reportIdStr}</span></div>
              <div className="font-mono text-[9px] text-gray-600">GENERATED: <span className="text-gray-400">{new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC</span></div>
              <div className="font-mono text-[9px] text-gray-600">PLATFORM: <span style={{ color: pColor }} className="uppercase font-bold">{platform}</span></div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">

          {/* ── Profile identity ── */}
          <div className="rounded-xl border border-gray-800/40 overflow-hidden bg-black/30">
            <div className="px-5 py-4">
              <div className="flex items-start gap-4">
                {profile.avatar ? (
                  <img src={profile.avatar} alt="" className="w-16 h-16 rounded-full border-2 object-cover shrink-0"
                    style={{ borderColor: pColor }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                ) : (
                  <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-black font-mono shrink-0"
                    style={{ background: `${pColor}20`, color: pColor, border: `2px solid ${pColor}40` }}>
                    {(profile.name || '?')[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h2 className="text-white font-bold text-xl font-mono">{profile.name || '—'}</h2>
                    {profile.verified && (
                      <span className="text-blue-400 text-sm font-bold">✓</span>
                    )}
                    <span className="font-mono text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-widest"
                      style={{ color: pColor, borderColor: `${pColor}50`, background: `${pColor}12` }}>
                      {platform}
                    </span>
                    {profile.category && (
                      <span className="font-mono text-[9px] text-gray-500 bg-gray-800/50 border border-gray-700/40 px-2 py-0.5 rounded">
                        {profile.category}
                      </span>
                    )}
                    {profile.username && profile.username !== profile.name && (
                      <span className="font-mono text-[10px] text-gray-500">@{profile.username}</span>
                    )}
                  </div>
                  {profile.bio && (
                    <p className="text-gray-400 text-sm leading-relaxed line-clamp-2 mb-3">{profile.bio}</p>
                  )}
                  <div className="flex flex-wrap gap-3">
                    {profile.followers > 0 && (
                      <div className="flex flex-col items-center bg-black/40 border border-gray-800/40 rounded-xl px-4 py-2.5 min-w-[72px]">
                        <span className="font-mono text-lg font-black text-green-400">{fmt(profile.followers)}</span>
                        <span className="font-mono text-[9px] text-gray-600 tracking-widest mt-0.5">FOLLOWERS</span>
                      </div>
                    )}
                    {(profile.following || 0) > 0 && (
                      <div className="flex flex-col items-center bg-black/40 border border-gray-800/40 rounded-xl px-4 py-2.5 min-w-[72px]">
                        <span className="font-mono text-lg font-black text-white">{fmt(profile.following!)}</span>
                        <span className="font-mono text-[9px] text-gray-600 tracking-widest mt-0.5">FOLLOWING</span>
                      </div>
                    )}
                    {(profile.post_count || 0) > 0 && (
                      <div className="flex flex-col items-center bg-black/40 border border-gray-800/40 rounded-xl px-4 py-2.5 min-w-[72px]">
                        <span className="font-mono text-lg font-black text-white">{fmt(profile.post_count!)}</span>
                        <span className="font-mono text-[9px] text-gray-600 tracking-widest mt-0.5">POSTS</span>
                      </div>
                    )}
                    {(profile.total_views || 0) > 0 && (
                      <div className="flex flex-col items-center bg-black/40 border border-gray-800/40 rounded-xl px-4 py-2.5 min-w-[72px]">
                        <span className="font-mono text-lg font-black text-red-400">{fmt(profile.total_views!)}</span>
                        <span className="font-mono text-[9px] text-gray-600 tracking-widest mt-0.5">TOTAL VIEWS</span>
                      </div>
                    )}
                    {engagement_stats.est_engagement_rate > 0 && (
                      <div className="flex flex-col items-center bg-black/40 border border-gray-800/40 rounded-xl px-4 py-2.5 min-w-[72px]">
                        <span className="font-mono text-lg font-black text-yellow-400">{engagement_stats.est_engagement_rate}%</span>
                        <span className="font-mono text-[9px] text-gray-600 tracking-widest mt-0.5">ENG RATE</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Influence + classification row ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Influence assessment */}
            <div className="rounded-xl border border-gray-800/40 bg-black/30 p-4 space-y-3">
              <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold">INFLUENCE ASSESSMENT</div>
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ReachBadge reach={influence_assessment?.reach || 'unknown'} />
                    {influence_assessment?.engagement_quality && (
                      <span className="font-mono text-[9px] text-gray-400 border border-gray-700/40 px-2 py-0.5 rounded uppercase tracking-wide">
                        {influence_assessment.engagement_quality} engagement
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[9px] text-gray-600">AUTHENTICITY</span>
                      <span className="font-mono text-xs font-bold" style={{
                        color: authScore >= 70 ? '#22c55e' : authScore >= 40 ? '#f59e0b' : '#ef4444'
                      }}>{authScore}/100</span>
                    </div>
                    <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{
                        width: `${authScore}%`,
                        background: authScore >= 70 ? '#22c55e' : authScore >= 40 ? '#f59e0b' : '#ef4444',
                      }} />
                    </div>
                  </div>
                </div>
                {authScore > 0 && (
                  <div className="shrink-0 w-16 h-16">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%"
                        data={gaugeData} startAngle={90} endAngle={-270}>
                        <RadialBar dataKey="value" cornerRadius={4} background={{ fill: '#1f2937' }} />
                      </RadialBarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              {influence_assessment?.notes && (
                <p className="text-gray-500 text-xs leading-relaxed">{influence_assessment.notes}</p>
              )}
            </div>

            {/* Subject classification */}
            <div className="rounded-xl border border-gray-800/40 bg-black/30 p-4 space-y-3">
              <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold">SUBJECT CLASSIFICATION</div>
              {subject_classification && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-black text-white uppercase tracking-wider">
                      {subject_classification.category}
                    </span>
                    <div className="flex-1 h-px bg-gray-800" />
                    <div className="text-right">
                      <div className="font-mono text-xs font-bold text-gray-300">{subject_classification.confidence}%</div>
                      <div className="font-mono text-[9px] text-gray-600">CONFIDENCE</div>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-purple-500/70" style={{ width: `${subject_classification.confidence}%` }} />
                  </div>
                  {subject_classification.reasoning && (
                    <p className="text-gray-500 text-xs leading-relaxed">{subject_classification.reasoning}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Executive summary ── */}
          {reportData.executive_summary && (
            <div className="rounded-xl border border-gray-800/40 bg-black/30 p-5">
              <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold mb-3 flex items-center gap-2">
                <span className="w-1.5 h-4 rounded-sm bg-green-500 shrink-0" />
                EXECUTIVE SUMMARY
              </div>
              <div className="text-gray-300 text-sm leading-[1.8] whitespace-pre-line">
                {reportData.executive_summary}
              </div>
            </div>
          )}

          {/* ── Engagement analytics + post gallery ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Engagement chart */}
            {chartData.length > 0 && (
              <div className="rounded-xl border border-gray-800/40 bg-black/30 p-4">
                <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold mb-4">
                  ENGAGEMENT PER POST ({chartData.length} POSTS)
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                    <XAxis dataKey="index" tick={{ fontSize: 9, fill: '#6b7280', fontFamily: 'monospace' }}
                      tickLine={false} axisLine={false} label={{ value: 'post #', position: 'insideBottom', offset: -2, fontSize: 8, fill: '#4b5563' }} />
                    <YAxis tick={{ fontSize: 9, fill: '#6b7280', fontFamily: 'monospace' }} tickLine={false}
                      axisLine={false} tickFormatter={fmt} width={40} />
                    {hasViews && (
                      <YAxis yAxisId="views" orientation="right" tick={{ fontSize: 9, fill: '#6b7280', fontFamily: 'monospace' }}
                        tickLine={false} axisLine={false} tickFormatter={fmt} width={40} />
                    )}
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="likes" name="Likes" fill="#22c55e" opacity={0.85} radius={[2, 2, 0, 0]} />
                    <Line dataKey="comments" name="Comments" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    {hasViews && (
                      <Line yAxisId="views" dataKey="views" name="Views" stroke="#f59e0b" strokeWidth={1.5}
                        strokeDasharray="4 2" dot={false} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-4 mt-2 justify-center">
                  <div className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
                    <span className="w-3 h-2.5 rounded-sm bg-green-500/80 inline-block" />Likes
                  </div>
                  <div className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
                    <span className="w-3 h-0.5 bg-blue-500 inline-block" />Comments
                  </div>
                  {hasViews && (
                    <div className="flex items-center gap-1.5 font-mono text-[9px] text-gray-500">
                      <span className="w-3 h-0.5 bg-yellow-500 inline-block border-dashed" />Views
                    </div>
                  )}
                </div>

                {/* Avg stats below chart */}
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {[
                    { label: 'AVG LIKES', value: engagement_stats.avg_likes, color: 'text-green-400' },
                    { label: 'AVG COMMENTS', value: engagement_stats.avg_comments, color: 'text-blue-400' },
                    { label: 'AVG VIEWS', value: engagement_stats.avg_views, color: 'text-yellow-400' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="bg-black/40 border border-gray-800/30 rounded-lg px-3 py-2 text-center">
                      <div className={`font-mono text-sm font-black ${color}`}>{fmt(value)}</div>
                      <div className="font-mono text-[8px] text-gray-600 tracking-widest mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Post gallery */}
            {thumbnails.length > 0 && (
              <div className="rounded-xl border border-gray-800/40 bg-black/30 p-4">
                <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold mb-4">
                  POST GALLERY ({thumbnails.length})
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {thumbnails.map((src, i) => {
                    const post = engagement_stats.per_post[i]
                    return (
                      <div key={i} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-900">
                        <img src={src} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                        {post && (
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-0.5">
                            {post.likes > 0 && <span className="font-mono text-[8px] text-green-300">♥ {fmt(post.likes)}</span>}
                            {post.views > 0 && <span className="font-mono text-[8px] text-yellow-300">▶ {fmt(post.views)}</span>}
                            {post.comments > 0 && <span className="font-mono text-[8px] text-blue-300">💬 {fmt(post.comments)}</span>}
                          </div>
                        )}
                        <div className="absolute top-1 left-1 font-mono text-[7px] text-black bg-white/70 rounded px-1">
                          #{i + 1}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Content themes ── */}
          {reportData.content_themes?.length > 0 && (
            <div className="rounded-xl border border-gray-800/40 bg-black/30 p-4">
              <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold mb-3">CONTENT THEMES</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {reportData.content_themes.map((t, i) => (
                  <div key={i} className="flex items-start gap-2.5 bg-black/40 border border-gray-800/30 rounded-xl px-3 py-2.5">
                    <FreqDot freq={t.frequency} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-white font-mono text-xs font-bold">{t.theme}</span>
                        <span className={`font-mono text-[8px] uppercase tracking-widest ${t.frequency === 'high' ? 'text-green-500' : t.frequency === 'medium' ? 'text-yellow-500' : 'text-gray-600'}`}>
                          {t.frequency}
                        </span>
                      </div>
                      <p className="text-gray-500 text-[10px] leading-relaxed">{t.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Behavioral + posting pattern ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {reportData.behavioral_profile && (
              <div className="rounded-xl border border-gray-800/40 bg-black/30 p-4">
                <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-4 rounded-sm bg-blue-500 shrink-0" />
                  BEHAVIORAL PROFILE
                </div>
                <p className="text-gray-400 text-sm leading-[1.7]">{reportData.behavioral_profile}</p>
              </div>
            )}
            {reportData.posting_pattern_analysis && (
              <div className="rounded-xl border border-gray-800/40 bg-black/30 p-4">
                <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-4 rounded-sm bg-purple-500 shrink-0" />
                  POSTING PATTERN
                </div>
                <p className="text-gray-400 text-sm leading-[1.7]">{reportData.posting_pattern_analysis}</p>
              </div>
            )}
          </div>

          {/* ── Top hashtags ── */}
          {reportData.top_hashtags?.length > 0 && (
            <div className="rounded-xl border border-gray-800/40 bg-black/30 p-4">
              <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold mb-3">TOP HASHTAGS</div>
              <div className="flex flex-wrap gap-2">
                {reportData.top_hashtags.map((tag, i) => {
                  const hues = ['bg-blue-900/40 text-blue-300 border-blue-800/40', 'bg-teal-900/40 text-teal-300 border-teal-800/40',
                    'bg-purple-900/40 text-purple-300 border-purple-800/40', 'bg-pink-900/40 text-pink-300 border-pink-800/40',
                    'bg-green-900/40 text-green-300 border-green-800/40']
                  return (
                    <span key={i} className={`font-mono text-[10px] px-2.5 py-1 rounded-full border ${hues[i % hues.length]}`}>
                      #{tag.replace(/^#/, '')}
                    </span>
                  )
                })}
              </div>
            </div>
          )}

          {/* ── Key findings ── */}
          {reportData.key_findings?.length > 0 && (
            <div className="rounded-xl border border-gray-800/40 bg-black/30 p-4">
              <div className="font-mono text-[9px] text-gray-500 tracking-widest font-bold mb-3 flex items-center gap-2">
                <span className="w-1.5 h-4 rounded-sm bg-yellow-500 shrink-0" />
                KEY FINDINGS
              </div>
              <ol className="space-y-2">
                {reportData.key_findings.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                    <span className="font-mono text-[10px] font-black text-yellow-500 shrink-0 mt-0.5 w-5">{i + 1}.</span>
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* ── Risk indicators ── */}
          {reportData.risk_indicators?.length > 0 && (
            <div className="rounded-xl border border-red-900/20 bg-red-950/10 p-4">
              <div className="font-mono text-[9px] text-red-500/70 tracking-widest font-bold mb-3 flex items-center gap-2">
                <span className="w-1.5 h-4 rounded-sm bg-red-500 shrink-0" />
                RISK INDICATORS
              </div>
              <div className="space-y-2">
                {reportData.risk_indicators.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 bg-black/40 border border-gray-800/30 rounded-lg px-3 py-2.5">
                    <SeverityBadge severity={r.severity} />
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-mono text-xs font-bold mb-0.5">{r.indicator}</div>
                      <p className="text-gray-500 text-[10px] leading-relaxed">{r.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── IOCs ── */}
          {reportData.iocs?.length > 0 && (
            <div className="rounded-xl border border-orange-900/20 bg-black/30 p-4">
              <div className="font-mono text-[9px] text-orange-500/70 tracking-widest font-bold mb-3 flex items-center gap-2">
                <span className="w-1.5 h-4 rounded-sm bg-orange-500 shrink-0" />
                INDICATORS OF COMPROMISE
              </div>
              <div className="space-y-1.5">
                {reportData.iocs.map((ioc, i) => (
                  <div key={i} className="flex items-start gap-3 px-3 py-2 bg-black/40 border border-gray-800/30 rounded-lg">
                    <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded bg-orange-900/40 text-orange-300 border border-orange-800/30 uppercase tracking-wide shrink-0">
                      {ioc.type}
                    </span>
                    <code className="font-mono text-xs text-orange-200 break-all flex-1">{ioc.value}</code>
                    {ioc.context && <span className="text-gray-600 text-[10px] shrink-0 max-w-[120px] truncate">{ioc.context}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Report footer ── */}
          <div className="border-t border-gray-800/40 pt-4 flex items-center justify-between gap-4">
            <div className="font-mono text-[9px] text-gray-700">
              SYCEK OSINT PLATFORM · CONFIDENTIAL · FOR AUTHORIZED USE ONLY
            </div>
            <div className="font-mono text-[9px] text-gray-700">
              {reportData.posts_analyzed} posts analysed · {new Date().toISOString().slice(0, 10)}
            </div>
          </div>

        </div>
      </div>
    </motion.div>
  )
}
